import { hasOwn } from './domain.js';
/**
 * A real, active person who counts toward filled headcount, FTE and cost.
 *
 * This is the "one population rule" the whole app applies, so any number appearing twice is the
 * same number: open (unfilled) positions and inactive people are excluded everywhere.
 */
export function countsInHeadcount(p) {
    return !p.openPosition && !p.inactive;
}
/** Excluded from aggregates for a different reason: attrition recorded at a data update. */
export function countsInAggregates(p) {
    return !p.isLeaver && countsInHeadcount(p);
}
/**
 * The FX factor for one person: their rate into the reporting currency, or exactly 1.
 *
 * `lookup` already encodes the three spellings of "no tier" (absent key, null, '') as null, and a
 * stored rate of 0 or worse must ALSO be identity rather than a zeroing factor: the handler refuses
 * writing one, but `mergeAssumptions` deliberately does not bound imported values, and a factor that
 * zeroed salaries on a bad import would be hundreds of silently wrong numbers that read as right.
 */
export function fxFactor(p, assumptions) {
    const rate = lookup(assumptions.fxRates, p.currency);
    return rate != null && rate > 0 ? rate : 1;
}
/**
 * A salary as an aggregate reads it: the stored amount, in the reporting currency.
 *
 * The one door for currency the way `loadedCost` is the one door for loading. Every engine that sums
 * salaries goes through here (or through a caller that does); the ONLY reads that stay stored-value
 * are the workbook's round-trip cell, the person panel's own field, and filter comparisons — each a
 * surface about the person's contract rather than a total.
 */
export function fxSalary(p, assumptions) {
    return (Number(p.salary) || 0) * fxFactor(p, assumptions);
}
/**
 * The loaded-cost multiplier that applies to THIS person: their country's tier, or the global.
 *
 * The single global multiplier is the crudest assumption in the product — Belgian employer charges
 * and US employer charges are not one number — and this is the severance-grid chain applied to it,
 * one tier deep. An imported zero or junk tier falls through to the global rather than zeroing a
 * country, the same defensive read as `fxFactor`.
 */
export function loadedMultiplierOf(p, assumptions) {
    const byCountry = lookup(assumptions.loadedCostMultiplierByCountry, p.country);
    if (byCountry != null && byCountry > 0)
        return byCountry;
    return Number(assumptions.loadedCostMultiplier) || 1;
}
/** Fully-loaded annual cost. Economics surfaces only. */
export function loadedCost(p, assumptions) {
    return fxSalary(p, assumptions) * loadedMultiplierOf(p, assumptions);
}
/**
 * The month THIS person's country closes, counted from Day 1. Not money — but the same tier door
 * as `loadedMultiplierOf`, kept beside it so the two grids can never drift on what an absent,
 * blank or junk tier means: no usable tier, closes at Day 1 (month 0). Whole months, because the
 * writer refuses fractions and an imported half-month would otherwise put a step between two bars.
 * Clamped one under the 120-month axis ceiling (`horizonOver`'s bound, which is also the TSA
 * clock's) so the accrual step at k+1 is always drawable — an imported tier beyond the chart reads
 * as the latest drawable close rather than as a carrier whose step no axis can ever show.
 */
export function closeMonthOf(p, assumptions) {
    const tier = lookup(assumptions.closeMonthByCountry, p.country);
    if (tier != null && tier > 0)
        return Math.min(119, Math.round(tier));
    return 0;
}
/**
 * Severance months for one person: seniority > country > global default, where a blank or missing
 * value at a tier inherits from the next.
 */
export function severanceMonths(p, assumptions) {
    const bySeniority = lookup(assumptions.severanceBySeniority, p.seniority);
    if (bySeniority != null)
        return bySeniority;
    const byCountry = lookup(assumptions.severanceByCountry, p.country);
    if (byCountry != null)
        return byCountry;
    return Number(assumptions.severanceMonthsDefault) || 0;
}
/**
 * WHICH rule produced `severanceMonths` — the same chain, resolved once.
 *
 * A panel that prints only the answer leaves the reader unable to tell a country override from the
 * global default, so the obvious question ("why is this person twelve months and that one six?") has
 * no answer on screen. Derived here rather than in the panel so the two can never disagree about the
 * order of the chain, and so that the seniority-beats-country precedence is stated in exactly one
 * place.
 */
export function severanceTier(p, assumptions) {
    if (lookup(assumptions.severanceBySeniority, p.seniority) != null)
        return 'seniority';
    if (lookup(assumptions.severanceByCountry, p.country) != null)
        return 'country';
    return 'default';
}
function lookup(grid, key) {
    if (key == null || key === '')
        return null;
    if (!hasOwn(grid, key))
        return null;
    const raw = grid[key];
    if (raw == null || raw === '')
        return null;
    return Number(raw) || 0;
}
/** One-off severance: months × monthly BASE salary. Unloaded, but converted — see the header. */
export function severanceCostOf(p, assumptions) {
    return (fxSalary(p, assumptions) / 12) * severanceMonths(p, assumptions);
}
/** One-off hiring cost of a planned position: a percentage of annual base salary. */
export function hiringCostOf(p, assumptions) {
    return (fxSalary(p, assumptions) * (Number(assumptions.hiringCostPct) || 0)) / 100;
}
/**
 * One-off retention for a person who must stay to their own exit: a percentage of annual base
 * salary, unloaded and converted like severance. Zero (the default) prices nothing.
 */
export function retentionCostOf(p, assumptions) {
    return (fxSalary(p, assumptions) * (Number(assumptions.retentionPct) || 0)) / 100;
}
/**
 * An edited Open Position / Inactive status counts as set unless it is an explicit off-word.
 * Ported verbatim from statusIsSet() — the exact word list is what makes old files keep working.
 */
export function statusIsSet(value) {
    const s = String(value == null ? '' : value)
        .trim()
        .toLowerCase();
    return s !== '' && s !== 'filled' && s !== 'active' && s !== 'no' && s !== 'false' && s !== '0' && s !== '—';
}
//# sourceMappingURL=cost.js.map